/**
 * 习惯月历：展示当月打卡网格，点击日期快速打卡/撤销（upsert log）。
 * date 格式 'YYYY-MM-DD'，与契约一致。
 */
import { useMemo, useState } from 'react';
import { useHabitLogs, useUpsertHabitLog } from '../hooks/useTasks';
import type { Habit } from '../lib/types';
import { cn, dateStr, todayStr } from '../lib/utils';
import { Button, ErrorBox, LoadingBlock, errMsg } from './ui';
import { Icon } from './icons';

interface HabitCalendarProps {
  habit: Habit | undefined;
}

function monthMatrix(year: number, month: number): Date[] {
  // month: 0-11；返回当月网格（含前置补空的周一起始）
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // 周一=0
  const days: Date[] = [];
  const start = new Date(year, month, 1 - startOffset);
  for (let i = 0; i < 42; i += 1) {
    days.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return days;
}

export function HabitCalendar({ habit }: HabitCalendarProps) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-11
  const [error, setError] = useState<string | null>(null);

  const from = dateStr(new Date(year, month, 1));
  const to = dateStr(new Date(year, month + 1, 0));
  const { data: logs, isLoading } = useHabitLogs(habit?.id, from, to);
  const upsertLog = useUpsertHabitLog();

  const days = useMemo(() => monthMatrix(year, month), [year, month]);
  const logMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of logs ?? []) m.set(l.date, l.count);
    return m;
  }, [logs]);

  function prevMonth() {
    if (month === 0) {
      setYear((y) => y - 1);
      setMonth(11);
    } else setMonth((m) => m - 1);
  }
  function nextMonth() {
    if (month === 11) {
      setYear((y) => y + 1);
      setMonth(0);
    } else setMonth((m) => m + 1);
  }

  async function toggleDay(date: string) {
    if (!habit) return;
    setError(null);
    const current = logMap.get(date) ?? 0;
    const target = habit.targetCount ?? 1;
    const next = current >= target ? 0 : target;
    try {
      await upsertLog.mutateAsync({
        habitId: habit.id,
        body: { date, count: next },
      });
    } catch (e) {
      setError(errMsg(e));
    }
  }

  if (!habit) {
    return <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-400 dark:border-slate-700">选择左侧习惯查看打卡日历</div>;
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <Icon name="calendar" className="h-4 w-4 text-indigo-500" />
          {habit.name} · 打卡日历
        </h3>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={prevMonth} aria-label="上一月">
            <Icon name="chevronLeft" className="h-4 w-4" />
          </Button>
          <span className="w-24 text-center text-sm font-medium">
            {year} 年 {month + 1} 月
          </span>
          <Button size="sm" variant="ghost" onClick={nextMonth} aria-label="下一月">
            <Icon name="chevronRight" className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <ErrorBox error={error} className="mb-3" />
      {isLoading ? (
        <LoadingBlock text="加载日志…" />
      ) : (
        <>
          <div className="mb-1 grid grid-cols-7 gap-1 text-center text-xs text-slate-400">
            {['一', '二', '三', '四', '五', '六', '日'].map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const ds = dateStr(d);
              const inMonth = d.getMonth() === month;
              const count = logMap.get(ds) ?? 0;
              const target = habit.targetCount ?? 1;
              const done = count >= target;
              const isToday = ds === todayStr();
              return (
                <button
                  key={ds}
                  type="button"
                  onClick={() => inMonth && toggleDay(ds)}
                  disabled={!inMonth}
                  className={cn(
                    'aspect-square rounded-lg border text-xs transition-colors',
                    inMonth
                      ? 'border-slate-200 hover:border-indigo-400 dark:border-slate-800'
                      : 'border-transparent text-slate-300 dark:text-slate-700',
                    done && 'border-transparent bg-emerald-500 text-white hover:bg-emerald-600',
                    isToday && !done && 'border-indigo-500 font-semibold text-indigo-600 dark:text-indigo-400',
                  )}
                  title={`${ds}${count ? ` · ${count} 次` : ''}`}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <span className="inline-block h-3 w-3 rounded bg-emerald-500" /> 已达成
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-3 w-3 rounded border border-indigo-500" /> 今天
            </span>
            <span>点击日期打卡（{habit.targetCount ?? 1} 次/日）</span>
          </div>
        </>
      )}
    </div>
  );
}
